const STORAGE_KEY = "tellescope:pendingPartnerRedirect";
const DEFAULT_TTL_MS = 10 * 60 * 1000;

interface StoredEntry {
  url: string;
  expiresAt: number;
}

const isStoredEntry = (value: unknown): value is StoredEntry =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as Record<string, unknown>).url === "string" &&
  typeof (value as Record<string, unknown>).expiresAt === "number";

const safeGetStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readEntry = (): StoredEntry | null => {
  const storage = safeGetStorage();
  if (!storage) return null;
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredEntry(parsed)) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    if (parsed.expiresAt <= Date.now()) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const setPendingPartnerRedirect = (url: string, ttlMs: number = DEFAULT_TTL_MS): void => {
  const storage = safeGetStorage();
  if (!storage) return;
  const entry: StoredEntry = { url, expiresAt: Date.now() + ttlMs };
  storage.setItem(STORAGE_KEY, JSON.stringify(entry));
};

export const peekPendingPartnerRedirect = (): string | null => {
  const entry = readEntry();
  return entry?.url ?? null;
};

export const consumePendingPartnerRedirect = (): string | null => {
  const entry = readEntry();
  if (!entry) return null;
  const storage = safeGetStorage();
  storage?.removeItem(STORAGE_KEY);
  return entry.url;
};

export const clearPendingPartnerRedirect = (): void => {
  const storage = safeGetStorage();
  storage?.removeItem(STORAGE_KEY);
};
